import * as React from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CreateButtonWithModal from '../components/common/CreateButtonWithModal';
import BlackWhiteSwitch from '../components/common/BlackWhiteSwitch';
import DeleteButton from '../components/common/DeleteButton';
import EditModal from '../components/common/EditModal';

interface Column {
  id: keyof Data | 'action';
  label: string;
  minWidth?: number;
  align?: 'right' | 'center' | 'left';
}

interface Data {
  code: string;
  type: string;
  status: string;
  createdBy: string;
}

const columns: readonly Column[] = [
  { id: 'type', label: 'Service Type', minWidth: 150 },
  { id: 'status', label: 'Status', minWidth: 100 },
  { id: 'createdBy', label: 'Created By', minWidth: 120 },
  { id: 'action', label: 'Action', minWidth: 100, align: 'center' },
];

function createData(code: string, type: string, status: string, createdBy: string): Data {
  return { code, type, status, createdBy };
}

const initialRows: Data[] = [
  createData('001', 'Flight', 'Active', 'Admin'),
  createData('002', 'Hotel', 'Active', 'Manager'),
  createData('003', 'Transfer', 'Inactive', 'Emma'),
  createData('004', 'Activity', 'Active', 'John'),
];

const Services = () => {
  const [rows, setRows] = React.useState<Data[]>(initialRows);
  const [filteredRows, setFilteredRows] = React.useState<Data[]>(initialRows);
  const [editOpen, setEditOpen] = React.useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = React.useState<number | null>(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterBy, setFilterBy] = React.useState('');

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSearch = () => {
    if (!filterBy || !searchTerm) {
      setFilteredRows(rows);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    const filtered = rows.filter((row) =>
      row[filterBy as keyof Data].toLowerCase().includes(lowerSearch)
    );
    setFilteredRows(filtered);
  };

  const handleEditSave = (updatedData: Data) => {
    if (selectedRowIndex === null) return;
    const updatedRows = [...rows];
    updatedRows[selectedRowIndex] = updatedData;
    setRows(updatedRows);
    setFilteredRows(updatedRows);
    setEditOpen(false);
  };

  const handleDeleteRow = (code: string) => {
    const updatedRows = rows.filter((row) => row.code !== code);
    setRows(updatedRows);
    setFilteredRows(updatedRows);
  };

  const renderStatusButton = (status: string) => (
    <Button
      variant="contained"
      size="small"
      sx={{
        backgroundColor: status === 'Active' ? '#1cad2d' : '#e31a24',
        color: 'white',
        fontWeight: 'bold',
        textTransform: 'none',
        '&:hover': {
          backgroundColor: status === 'Active' ? '#2e7d32' : '#c62828',
        },
      }}
    >
      {status}
    </Button>
  );

  const renderActions = (row: Data, index: number) => (
    <>
      <IconButton
        color="primary"
        size="small"
        onClick={() => {
          setSelectedRowIndex(index);
          setEditOpen(true);
        }}
      >
        <EditIcon />
      </IconButton>
      <DeleteButton
        onDelete={() => handleDeleteRow(row.code)}
        confirmMessage="Do you really want to delete this item?"
      />
    </>
  );

  React.useEffect(() => {
    setFilteredRows(rows);
  }, [rows]);

  return (
    <>
      <Box className="headingBtnWrap">
        <Typography variant="h1">Services</Typography>
        <CreateButtonWithModal modalTitle="Services">
          <Box className="modalFormWrap">
            <Box component="form" className="modalForm">
              <div className="form-control inputFieldWrap">
                <label>Service Type</label>
                <input type="text" className="inputField" />
              </div>
            </Box>
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" className="formBtn">
                Save
              </Button>
            </Box>
          </Box>
          <BlackWhiteSwitch />
        </CreateButtonWithModal>
      </Box>

      {/* <Box
        display="flex"
        alignItems="center"
        gap={2}
        p={2}
        sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}
        className="searchFilterDropdownWrap"
      >
        <TextField
          fullWidth
          variant="outlined"
          className="searchFilterInput"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={handleSearch} edge="start">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 120 }}>
          <Select
            labelId="filter-label"
            value={filterBy}
            onChange={(e) => setFilterBy(e.target.value)}
            displayEmpty
            className="searchFilterDropdown"
          >
            <MenuItem value=""><em>Select Filter</em></MenuItem>
            <MenuItem value="type">Service Type</MenuItem>
            <MenuItem value="status">Status</MenuItem>
            <MenuItem value="createdBy">Created By</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" className="searchBtn" onClick={handleSearch}>
          Search
        </Button>
      </Box> */}

      <Paper sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="services table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => (
                  <TableRow hover key={row.code}>
                    {columns.map((column) => {
                      if (column.id === 'status') {
                        return (
                          <TableCell key={column.id}>
                            {renderStatusButton(row.status)}
                          </TableCell>
                        );
                      }
                      if (column.id === 'action') {
                        return (
                          <TableCell key={column.id} align="center">
                            {renderActions(row, rowIndex)}
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={column.id}>
                          {row[column.id as keyof Data]}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Edit Modal */}
      {selectedRowIndex !== null && (
        <EditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          initialData={rows[selectedRowIndex]}
          onSave={handleEditSave}
          excludeFields={['action', 'status']}
          readOnlyFields={['createdBy']}
          title="Edit Services"
        />
      )}
    </>
  );
};

export default Services;
